import React, { useState, useRef, useEffect } from 'react'
import { View } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import { setDomAttributes } from '../../utils/setDomAttributes'

import SubTasksView from './Subtask/SubTasksView'
import { useTaskHierarchy, taskHierarchyStyles } from './TaskHierarchy'
import { setFocusedTaskItem, unsetUploadedNewSubtask } from '../../redux/actions'
import TaskIndicator from './TaskIndicator'
import TaskItem from './TaskItem'
import store from '../../redux/store'
import ProjectHelper from '../SettingsView/ProjectsSettings/ProjectHelper'
import { objectIsLockedForUser } from '../Guides/guidesHelper'
import { TASK_ASSIGNEE_ASSISTANT_TYPE } from './Utils/TasksHelper'
import useProjectPostponePreview from '../../hooks/useProjectPostponePreview'
import { projectTaskPreview } from '../../utils/backends/Tasks/optimisticProjectPostpone'

export default function ParentTaskContainer({
    projectId,
    task: sourceTask,
    isActiveOrganizeMode,
    expandOrContractSubtasks,
    isObservedTask,
    isToReviewTask,
    isSuggested,
    checked,
    subtaskList,
    provided,
    inParentGoal,
    containerStyle,
    isPending,
    inTodayOpenList,
}) {
    const dispatch = useDispatch()
    const postponeEntry = useProjectPostponePreview(projectId)
    const { task, hidden } = projectTaskPreview(
        sourceTask,
        isObservedTask || isToReviewTask || isSuggested || isPending ? null : postponeEntry
    )
    const taskHierarchy = useTaskHierarchy()
    const loggedUserId = useSelector(state => state.loggedUser.uid)
    const isMiddleScreen = useSelector(state => state.isMiddleScreen)
    const isFocusedTaskItem = useSelector(state => state.focusedTaskItem.id === task.id)
    const draggingParentTaskId = useSelector(state => state.draggingParentTaskId)
    const [inEditMode, setInEditMode] = useState(false)
    const [showSubTaskList, setShowSubTaskList] = useState(false)
    const [showSubTaskIndicator, setShowSubTaskIndicator] = useState(false)
    const [createSubtaskRequest, setCreateSubtaskRequest] = useState(0)
    const parentRef = useRef(null)
    const dismissibleRef = useRef(null)
    const taskItemRef = useRef(null)

    const setAriaTaskId = () => {
        setDomAttributes(parentRef.current, { 'aria-task-id': task.id })
        setDomAttributes(parentRef.current, { 'is-observed-task': isObservedTask ? 'true' : 'false' })
    }

    const toggleSubTaskList = () => {
        showSubTaskList ? hideSubtaskList() : showSubtaskList()
    }

    const showSubtaskList = () => {
        if (expandOrContractSubtasks) expandOrContractSubtasks(task.id, true)
        setShowSubTaskList(true)
    }

    const hideSubtaskList = () => {
        if (expandOrContractSubtasks) expandOrContractSubtasks(task.id, false)
        setShowSubTaskList(false)
        if (subtaskList.length === 0) setShowSubTaskIndicator(false)
    }

    const createSubtask = () => {
        showSubtaskList()
        setCreateSubtaskRequest(request => request + 1)
    }

    useEffect(() => {
        setAriaTaskId()
        return () => {
            if (expandOrContractSubtasks) expandOrContractSubtasks(task.id, false)
        }
    }, [])

    useEffect(() => {
        if (subtaskList.length > 0) {
            const { uploadedNewSubtask } = store.getState()
            if (uploadedNewSubtask) {
                showSubtaskList()
                dispatch(unsetUploadedNewSubtask())
            }
        }
    }, [subtaskList.length])

    useEffect(() => {
        if (showSubTaskList && subtaskList.length === 0 && isActiveOrganizeMode) {
            setShowSubTaskList(false)
            if (expandOrContractSubtasks) expandOrContractSubtasks(task.id, false)
        }
    }, [showSubTaskList, isActiveOrganizeMode, subtaskList.length])

    useEffect(() => {
        if (isFocusedTaskItem) {
            const { unlockedKeysByGuides } = store.getState().loggedUser
            const isLocked = objectIsLockedForUser(projectId, unlockedKeysByGuides, task.lockKey, task.userId)

            if (isLocked) {
                dispatch(setFocusedTaskItem('', false))
            } else {
                const { activeEditMode } = store.getState()
                if (!activeEditMode) dismissibleRef.current.openModal()
            }
        }
    }, [isFocusedTaskItem])

    const loggedUserIsTaskOwner = loggedUserId === task.userId
    const isAssistant = task.assigneeType === TASK_ASSIGNEE_ASSISTANT_TYPE

    const loggedUserCanUpdateObject =
        loggedUserIsTaskOwner || !ProjectHelper.checkIfLoggedUserIsNormalUserInGuide(projectId)

    return (
        <View
            ref={parentRef}
            style={[containerStyle, taskHierarchy && showSubTaskList && taskHierarchyStyles.taskFamily]}
        >
            {(loggedUserCanUpdateObject || subtaskList.length > 0) &&
            !hidden &&
            !isMiddleScreen &&
            (subtaskList.length > 0 || showSubTaskIndicator) &&
            !isAssistant ? (
                <TaskIndicator
                    inEditMode={inEditMode}
                    dismissibleRef={dismissibleRef.current}
                    toggleSubTaskList={toggleSubTaskList}
                    showSubTaskList={showSubTaskList}
                />
            ) : null}
            {!hidden && (
                <TaskItem
                    projectId={projectId}
                    task={task}
                    isObservedTask={isObservedTask}
                    isToReviewTask={isToReviewTask}
                    isActiveOrganizeMode={isActiveOrganizeMode}
                    provided={provided}
                    dismissibleRef={dismissibleRef}
                    taskItemRef={taskItemRef}
                    toggleSubTaskList={toggleSubTaskList}
                    subtaskList={subtaskList}
                    isSuggested={isSuggested}
                    checked={checked}
                    showSubTaskList={showSubTaskList}
                    setInEditMode={setInEditMode}
                    setShowSubTaskIndicator={setShowSubTaskIndicator}
                    inParentGoal={inParentGoal}
                    isPending={isPending}
                    createSubtask={createSubtask}
                    inTodayOpenList={inTodayOpenList}
                />
            )}

            {(!isActiveOrganizeMode && showSubTaskList) ||
            (isActiveOrganizeMode && subtaskList.length > 0 && draggingParentTaskId === task.id) ? (
                <SubTasksView
                    hideSubtaskList={hideSubtaskList}
                    showSubtaskList={showSubtaskList}
                    projectId={projectId}
                    parentTask={task}
                    subtaskList={subtaskList}
                    isActiveOrganizeMode={isActiveOrganizeMode}
                    isObservedTask={isObservedTask}
                    isToReviewTask={isToReviewTask}
                    isPending={isPending}
                    createSubtaskRequest={createSubtaskRequest}
                />
            ) : null}
        </View>
    )
}
